import React from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { useAuthContext } from '../AuthContext';

interface OtpScreenProps {
  onAuthenticated: () => Promise<void>;
}

export function OtpScreen({ onAuthenticated }: OtpScreenProps) {
  const { email, setScreen } = useAuthContext();
  const [otp, setOtp] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [resendState, setResendState] = React.useState<'idle' | 'sending' | 'sent'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!otp.trim()) return;

    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: otp.trim() });
      if (result.isSignedIn) {
        await onAuthenticated();
      } else {
        setError('Sign-in incomplete. Please try again.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Invalid code.';
      if (msg.toLowerCase().includes('expired')) {
        setError('Code expired — click Resend to get a new one.');
      } else {
        setError('Invalid code. Please check and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendState('sending');
    try {
      await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      });
      setResendState('sent');
      setOtp('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend. Please try again.');
      setResendState('idle');
    }
  };

  return (
    <div className="fs-auth-fullscreen">
      <div className="fs-auth-card">
        <img
          className="fs-auth-logo"
          src="https://static.focused.space/logo_landscape_1x.png"
          alt="Focused Space"
        />

        <div style={{ width: '100%' }}>
          <button className="fs-auth-back-btn" onClick={() => setScreen('email_input')}>
            ← Back
          </button>
        </div>

        <h1 className="fs-auth-heading">Check your email</h1>
        <p className="fs-auth-subheading">
          We sent a code to <strong>{email}</strong>
        </p>

        <form style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }} onSubmit={handleSubmit} noValidate>
          <input
            className={`fs-auth-input${error ? ' fs-auth-input-error' : ''}`}
            type="text"
            inputMode="numeric"
            placeholder="Enter code"
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value);
              setError(null);
            }}
            autoComplete="one-time-code"
            autoFocus
            disabled={loading}
          />

          {error && <p className="fs-auth-error">{error}</p>}

          <button
            type="submit"
            className="fs-auth-btn fs-auth-btn-primary"
            disabled={loading || !otp.trim()}
          >
            {loading ? 'Verifying…' : 'Verify code'}
          </button>
        </form>

        <div style={{ textAlign: 'center' }}>
          {resendState === 'sent' ? (
            <p className="fs-auth-subheading">Code resent! Check your email.</p>
          ) : (
            <span className="fs-auth-subheading">
              Didn&apos;t get it?{' '}
              <button
                className="fs-auth-btn-link"
                onClick={handleResend}
                disabled={resendState === 'sending'}
              >
                {resendState === 'sending' ? 'Sending…' : 'Resend'}
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
